/**
 * Phase 7.6 — GuidanceProvider.
 *
 * Single per-session cache of all guidance keys the platform has
 * fetched. Pages request keys via `useGuidance(key)`; the provider
 * batches the requests, fires one bulk call, caches the resolved
 * sentences, and replays them to every consumer.
 *
 * Why a provider instead of per-component fetches:
 *   * coalesces network round-trips (typical page asks 6-12 keys)
 *   * one place to refresh on language change
 *   * one place to instrument logging / metrics later
 */
import React, {
    createContext, useCallback, useContext, useEffect, useMemo,
    useRef, useState,
} from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";

const GuidanceCtx = createContext({
    register: () => {},
    get: () => null,
    loading: false,
});

export const GuidanceProvider = ({ children }) => {
    const { i18n } = useTranslation();
    const lang = (i18n.language || "en").split("-")[0];
    const [cache, setCache] = useState({});   // key -> resolved entry | null
    const [loading, setLoading] = useState(false);
    const pending = useRef(new Set());        // keys queued for next flush
    const inflight = useRef(false);
    const timer = useRef(null);

    // When language changes, drop the cache so subsequent requests
    // re-fetch in the new language.
    useEffect(() => {
        setCache({});
    }, [lang]);

    const flush = useCallback(async () => {
        if (inflight.current || pending.current.size === 0) return;
        const batch = Array.from(pending.current).slice(0, 64);
        pending.current = new Set(Array.from(pending.current).slice(64));
        inflight.current = true;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            batch.forEach((k) => params.append("keys", k));
            params.set("lang", lang);
            const res = await api.get(
                `/operational-base/guidance/bulk?${params.toString()}`,
            );
            const entries = res.data?.entries || {};
            setCache((prev) => ({ ...prev, ...entries }));
        } catch (_e) {
            // calm fail — pages render no tooltip rather than crashing
            const blank = {};
            batch.forEach((k) => { blank[k] = null; });
            setCache((prev) => ({ ...prev, ...blank }));
        } finally {
            inflight.current = false;
            setLoading(false);
            // Drain any keys queued during the flight.
            if (pending.current.size > 0) flush();
        }
    }, [lang]);

    const register = useCallback((key) => {
        if (!key) return;
        if (cache[key] !== undefined) return;       // already resolved
        if (pending.current.has(key)) return;       // already queued
        pending.current.add(key);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(flush, 40);      // micro-debounce
    }, [cache, flush]);

    const get = useCallback((key) => cache[key] ?? null, [cache]);

    const value = useMemo(
        () => ({ register, get, loading }),
        [register, get, loading],
    );

    return <GuidanceCtx.Provider value={value}>{children}</GuidanceCtx.Provider>;
};

/**
 * Subscribe to a guidance key. Returns the resolved entry once it
 * arrives, or `null` while loading / if the key is unregistered.
 *
 *   const guidance = useGuidance("analytics.briefing.intro");
 *   guidance?.what     // "This is your operational briefing — ..."
 *   guidance?.why
 *   guidance?.what_next
 *   guidance?.safe_to_explore
 *   guidance?.attention_level
 */
export const useGuidance = (key) => {
    const { register, get } = useContext(GuidanceCtx);
    useEffect(() => { register(key); }, [key, register]);
    return get(key);
};

export default GuidanceProvider;
